'use client';

import cx from 'classnames';
import { MouseEvent, ReactNode } from 'react';

import { alert } from '@flipster/utils/alert';

import { Button } from '../Button';
import CircleInfoFillIcon from '../assets/svgs/circle-info-fill.svg';
import SortUpIcon from '../assets/svgs/sort-up.svg';
import styles from './tableHeader.module.scss';
import { AlignType } from './types';

type OrderByType = 'asc' | 'desc' | undefined;

export interface Col {
  title: string;
  align?: AlignType;
  sortKey?: string;
  tooltip?: {
    icon?: ReactNode;
    title?: string;
    message: string;
  };
  className?: string;
  width?: number;
  minWidth?: number;
}

interface Props {
  columns: Col[];
  bold?: boolean;
  sortKey?: string;
  sortOrder?: OrderByType;
  onChangeSorting?: (sorting: [key: string | undefined, orderBy: OrderByType]) => unknown;
  className?: string;
}

export const TableHeader = ({ columns, bold, sortKey, sortOrder, onChangeSorting, className }: Props) => {
  const onClickSorting = (e: MouseEvent<HTMLButtonElement>) => {
    const key = e.currentTarget.value;
    if (key === sortKey && sortOrder === 'asc') {
      onChangeSorting?.([undefined, undefined]);
      return;
    }
    onChangeSorting?.([key, key !== sortKey ? 'desc' : 'asc']);
  };

  const isFixedWidth = columns.some((col) => col.width);

  return (
    <>
      {isFixedWidth && (
        <colgroup>
          {columns.map((col) => (
            <col key={`colgroup-${col.title}`} width={col.width} />
          ))}
        </colgroup>
      )}
      <thead className={cx(styles.wrapper, className, { [styles.bold]: bold })}>
        <tr>
          {columns.map((col) => {
            return (
              <th
                className={col.className}
                key={`th-${col.title}`}
                // safari에서는 colgroup의 minWidth가 작동하지 않아, 각 th마다 minWidth를 지정해줘야 함
                style={{ minWidth: col.minWidth }}
              >
                <div className={styles[col.align ?? 'left']}>
                  {!col.sortKey && <span className={styles.title}>{col.title}</span>}
                  {col.sortKey && (
                    <button type='button' value={col.sortKey} onClick={onClickSorting} className={styles.sorting}>
                      <span className={cx(styles.title, { [styles.sorted]: !!sortOrder && sortKey === col.sortKey })}>
                        {col.title}
                      </span>
                      <div className={cx(styles.icon, { [styles[sortOrder!]]: sortOrder && sortKey === col.sortKey })}>
                        <span className={styles.up}>
                          <SortUpIcon />
                        </span>
                        <span className={styles.down}>
                          <SortUpIcon />
                        </span>
                      </div>
                    </button>
                  )}
                  {col.tooltip && (
                    <Button
                      shape='trans'
                      className={styles.tooltip}
                      onClick={() => {
                        alert.show({ title: col.tooltip?.title, message: col.tooltip?.message, confirmText: 'OK' });
                      }}
                    >
                      {col.tooltip.icon || <CircleInfoFillIcon role='tooltip' />}
                    </Button>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
    </>
  );
};
